import axios, { AxiosError } from 'axios';

// Centralized fallback messages for reuse and easy updates
const FALLBACK_GENERIC = 'Something went wrong. Please try again.';
const MSG_TIMEOUT =
	'Request timed out. Please check your connection and try again.';
const MSG_NETWORK =
	'Network error. Please check your internet connection and try again.';

// Matches provided declaration
type ErrorsField =
	| string
	| string[]
	| Record<string, string | string[]>
	| undefined;

type AxiosData = unknown;

function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

function formatDetails(detailMessages: string[]): string {
	return detailMessages.length ? `\n- ${detailMessages.join('\n- ')}` : '';
}

function collectDetailMessages(errors: ErrorsField): string[] {
	const details: string[] = [];

	if (!errors) return details;

	if (typeof errors === 'string') {
		if (isNonEmptyString(errors)) details.push(errors);
		return details;
	}

	if (Array.isArray(errors)) {
		for (const item of errors) {
			if (isNonEmptyString(item)) details.push(item);
		}
		return details;
	}

	// errors is a record
	for (const key of Object.keys(errors)) {
		const val = (errors as Record<string, string | string[]>)[key];
		if (Array.isArray(val)) {
			for (const v of val) {
				if (isNonEmptyString(v)) details.push(`${key}: ${v}`);
			}
		} else if (isNonEmptyString(val)) {
			details.push(`${key}: ${val}`);
		}
	}

	return details;
}

function extractPrimaryMessage(data: AxiosData): {
	primaryMessage: string;
	detailMessages: string[];
} {
	// If server returned a plain string, use it directly
	if (isNonEmptyString(data)) {
		return { primaryMessage: data, detailMessages: [] };
	}

	if (data && typeof data === 'object') {
		const anyData = data as Record<string, unknown>;
		const primary =
			(isNonEmptyString(anyData.message)
				? (anyData.message as string).trim()
				: '') ||
			(isNonEmptyString(anyData.title)
				? (anyData.title as string).trim()
				: '');

		const errors: ErrorsField = Object.prototype.hasOwnProperty.call(
			anyData,
			'errors',
		)
			? (anyData.errors as ErrorsField)
			: undefined;

		const detailMessages = collectDetailMessages(errors);
		return { primaryMessage: primary, detailMessages };
	}

	return { primaryMessage: '', detailMessages: [] };
}

function mapStatusToMessage(
	status: number | undefined,
	primaryMessage: string,
	detailMessages: string[],
): string {
	const detailText = formatDetails(detailMessages);

	switch (status) {
		case 400:
			return (
				primaryMessage ||
				(detailMessages.length
					? `Validation failed:${detailText}`
					: 'Bad request. Please check your input and try again.')
			);
		case 401:
			return (
				primaryMessage ||
				'Not authorized. Please sign in and try again.'
			);
		case 403:
			return (
				primaryMessage ||
				'You do not have permission to perform this action.'
			);
		case 404:
			return primaryMessage || 'The requested resource was not found.';
		case 408:
			return primaryMessage || 'The request timed out. Please try again.';
		case 409:
			return (
				primaryMessage ||
				'A conflict occurred. Please refresh and try again.'
			);
		case 413:
			return (
				primaryMessage ||
				'Payload too large. Please try uploading a smaller file.'
			);
		case 429:
			return (
				primaryMessage ||
				'Too many requests. Please wait a moment and try again.'
			);
		default:
			if (status && status >= 500) {
				return (
					primaryMessage || 'Server error. Please try again later.'
				);
			}
			return primaryMessage || FALLBACK_GENERIC;
	}
}

function normalizeAxiosError(axErr: AxiosError): string {
	// Network/timeout
	if (!axErr.response) {
		return axErr.code === 'ECONNABORTED' ? MSG_TIMEOUT : MSG_NETWORK;
	}

	const status = axErr.response.status;
	const data = axErr.response.data as AxiosData;

	const { primaryMessage, detailMessages } = extractPrimaryMessage(data);

	const message = mapStatusToMessage(status, primaryMessage, detailMessages);

	// If status mapping produced only generic fallback but Axios has a message, prefer Axios message
	return message === FALLBACK_GENERIC ? axErr.message || message : message;
}

export function normalizeError(err: unknown): string {
	if (axios.isAxiosError(err)) {
		return normalizeAxiosError(err as AxiosError);
	}

	if (err instanceof Error) {
		return err.message || FALLBACK_GENERIC;
	}

	if (typeof err === 'string') {
		return err || FALLBACK_GENERIC;
	}

	try {
		return JSON.stringify(err);
	} catch {
		return FALLBACK_GENERIC;
	}
}
